import type { Scene } from "@babylonjs/core";
import { $markerPanel, $markers, $markersFailed } from "../state/markers";
import { $panel, $plots, roleIn } from "../state/stores";
import { $activeTool, $confirmReady } from "../state/tools";
import { entityLabel } from "../world/rules";
import { type MarkerAim, markerTarget, toLocal } from "../world/markers";
import type { WorldSession } from "../session";
import type { HUD } from "../hud/hud";
import type { EntityManager } from "./entities";
import type { BuiltWorld } from "./worldBuild";

const RANGE = 30; // spec 2h §2.2

/** While $activeTool is "marker": aim a 30 m ray, prompt, and on confirm (left click, ✓) open the description form. */
export class MarkerMode {
  private obs: ReturnType<Scene["onBeforeRenderObservable"]["add"]> | null = null;
  private aim: MarkerAim | null = null;

  constructor(
    private readonly scene: Scene,
    private readonly session: WorldSession,
    private readonly world: BuiltWorld,
    private readonly entities: EntityManager,
    private readonly hud: HUD,
  ) {
    $activeTool.subscribe((t) => (t === "marker" ? this.start() : this.stop()));
  }

  private start(): void {
    if (!this.obs) this.obs = this.scene.onBeforeRenderObservable.add(() => this.track());
  }

  private stop(): void {
    if (!this.obs) return;
    this.scene.onBeforeRenderObservable.remove(this.obs);
    this.obs = null;
    this.aim = null;
    $confirmReady.set(false);
    this.hud.setPrompt(null);
  }

  private track(): void {
    const ray = this.session.player.camera.getForwardRay(RANGE);
    const hit = this.scene.pickWithRay(ray, (m) => m.isEnabled() && (m.metadata?.terrain === true || m.metadata?.plotFloor === true ||
      !!m.metadata?.entityId || m.name === "hall:floor" || m.name === "hall:porch"));
    const ent = this.entities.entityAt(hit?.pickedMesh);
    const point = hit?.hit && hit.pickedPoint ? { x: hit.pickedPoint.x, y: hit.pickedPoint.y, z: hit.pickedPoint.z } : null;
    this.aim = markerTarget(point && { point, entity: ent ? { id: ent.id, name: entityLabel(ent) } : null },
      $plots.get(), roleIn, $markers.get(), $markersFailed.get());
    this.hud.setPrompt(this.aim.prompt);
    $confirmReady.set(this.aim.ok);
  }

  confirm(): void {
    const a = this.obs ? this.aim : null;
    if (!a?.ok) return;
    $markerPanel.set({ mode: "create", plotId: a.plot.id, plotName: a.plot.name, pos: toLocal(a.plot, a.point, this.world.height),
      at: a.point, target: a.entity ? { entity_id: a.entity.id, name: a.entity.name } : null });
    $activeTool.set(null);
    $panel.set("marker");
  }
}
